// W1-13 — THE DICTATION LAW, PULLED OUT SO A GATE CAN RUN IT.
//
// HER FINDING: "Dictation shows nothing until the end, then dumps it all at once. Members must see words
// appearing AS THEY SPEAK." The cause was one line in `useDictation` — `rec.interimResults = false` — so
// the browser handed us nothing until it decided a phrase was finished, which in Chrome means at a pause.
// A member speaking a long thought watched an empty box.
//
// Turning interim results ON is the easy half. The hard half is the COMPOSITION, and it is the half that
// goes wrong in every hand-rolled dictation UI: an interim phrase grows word by word ("I", "I need", "I
// need a"), and each growth must REPLACE the last one rather than append to it, while every FINALIZED
// phrase is committed permanently. Get that wrong and the member watches her sentence duplicate itself.
//
// That law lives here, as a pure reducer over events, for the reason every law in `src/lib` lives here:
// **a law inside a TSX file cannot be imported by a `.mjs` gate.** `scripts/w1-dictation.mjs` drives the
// exact event sequence a browser emits and asserts the text the member would see at every step, with no
// microphone, no browser and no spend. This module imports NOTHING, which is the entry requirement.

// One spoken chunk joined onto what is already in the field. Her existing behaviour, kept exactly: a
// single space between, and a chunk landing in an empty field does not gain a leading space.
export function joinSpoken(base: string, chunk: string): string {
  const b = String(base || "").replace(/\s+$/, "");
  const c = String(chunk || "").trim();
  if (!c) return b;
  if (!b) return c;
  return `${b} ${c}`;
}

// `committed` is everything the field owns for good: whatever was typed before the mic opened, plus every
// finalized phrase. `live` is the browser's current guess, which is shown but never kept.
export type DictationState = { committed: string; live: string };
export type DictationEvent = { text: string; final: boolean };

// Dictation opens on whatever is already in the field, so a member can type half a sentence and speak the
// rest. Nothing already written is ever treated as speech.
export function dictationStart(current: string): DictationState {
  return { committed: String(current || ""), live: "" };
}

export function dictationApply(state: DictationState, event: DictationEvent): DictationState {
  const text = String(event?.text || "").trim();
  if (event?.final) {
    // A finalized phrase is committed, and the live guess it replaces is cleared in the same step.
    return { committed: text ? joinSpoken(state.committed, text) : state.committed, live: "" };
  }
  // A new guess REPLACES the previous one. An empty guess clears it, which is what stops a stale word
  // hanging in the field after the browser finalizes a phrase.
  return { committed: state.committed, live: text };
}

// What the member sees in the box right now.
export function dictationText(state: DictationState): string {
  return state.live ? joinSpoken(state.committed, state.live) : state.committed;
}

// Stopping keeps what is on screen: if the browser never finalized the last guess, the member's words are
// hers to edit, not something to throw away because a timeout arrived first.
export function dictationSettle(state: DictationState): DictationState {
  return { committed: dictationText(state), live: "" };
}

// The one browser interface this uses, named here so App.tsx does not carry a five-line structural type.
export type SpeechResultEvent = {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean } & ArrayLike<{ transcript: string }>>;
};

// The browser hands back a run of results from `resultIndex`: some finalized, at most one still a guess.
// Split them the way the spec describes, so the reducer above is fed one final and one live string.
export function splitSpeechResults(
  event: SpeechResultEvent,
  through = -1,
): { final: string; live: string; through: number; finals: string[] } {
  let final = "", live = "";
  // IT #314: each final ALSO kept on its own, because a phone's recogniser can hand back finals that
  // each repeat the one before, and only one at a time can be compared (see `joinFinals` below).
  const finals: string[] = [];
  const results = event?.results;
  if (!results) return { final, live, through, finals };
  // >>> **IT 163 — THE COMMIT IS IDEMPOTENT BY RESULT INDEX NOW, AND THAT IS THE WHOLE FIX.** <<<
  //
  // A member reported that dictation "writes double words repeatedly and the same sentence twice".
  // It did. This walked from `event.resultIndex` and every final it found was APPENDED to the
  // committed text by `dictationApply` — and `resultIndex` is not the promise it looks like. Several
  // engines re-report earlier results, or hand back 0, on a later event; the recogniser is also
  // RESTARTED on every silence timeout by design, and a phrase straddling that restart is reported
  // by both. A final that arrives twice was written twice, and a whole sentence re-reported was
  // written twice. Exactly what she described.
  //
  // `through` is the count of leading results already committed. Finals become final IN ORDER, so a
  // prefix count is a sound identity for them, and starting from it makes a re-delivery a no-op
  // instead of a second sentence. `-1` means "trust the event", which is only ever the first call.
  const from = through >= 0 ? Math.max(through, 0) : (Number(event.resultIndex) || 0);
  let next = through;
  for (let i = from; i < results.length; i += 1) {
    const result = results[i];
    if (!result) continue;
    const transcript = result[0]?.transcript || "";
    if (result.isFinal) { final += transcript; finals.push(transcript); next = i + 1; }
    else live += transcript;
  }
  return { final, live, through: next, finals };
}

// ---------------------------------------------------------------------------
// IT 163b — **THE HALF `through` COULD NOT REACH: A PHRASE THAT STRADDLES A RESTART.**
//
// >>> **A MEMBER SAID *"its still typing double when i speak"* ON 2026-09-04**, in thread 758, after
// IT 163 shipped. She is right, and this module's own comment says why: *"the recogniser is also
// RESTARTED on every silence timeout by design, and a phrase straddling that restart is reported by
// both."* `through` is a prefix count into ONE recogniser's result list, so it makes a re-delivery
// inside that list a no-op — and a fresh recogniser starts a fresh list at zero, where a count from
// the old one would SKIP real speech. Resetting it per recogniser was the right call and it closes
// the skip; it cannot close the straddle, because the two reports live in two different lists.
//
// **WHAT THE STRADDLE LOOKS LIKE.** She says *"and then I went to the store"*. The old recogniser
// finalizes *"and then I went"* and the silence timeout fires; the new one hears the tail with the
// browser's own audio buffer behind it and finalizes *"and then I went to the store"*. Committed
// naively that is *"and then I went and then I went to the store"* — her exact words for it.
//
// **SO THE OVERLAP IS DROPPED, AND ONLY AT THE BOUNDARY.** This is asked ONCE per restart, on the
// first final a new recogniser produces, and never inside a running list — because a member who
// really does say *"no no"* mid-sentence must keep both, and a de-duplicator that ran on every final
// would quietly edit her. **The trade is named rather than hidden:** if she repeats a word ACROSS a
// silence long enough for the browser to give up, one copy is dropped. That is a rarer wrong than the
// one she is reporting, and it is the direction R19 says this error is allowed to go.
//
// Whole WORDS only, and compared with punctuation and case set aside — engines re-report the same
// phrase with different capitalisation and a comma that was not there the first time.
// ---------------------------------------------------------------------------

/** Words, for comparison only. The member's own text is never rewritten from this. */
const compareWords = (s: string) =>
  String(s || "").toLowerCase().replace(/[^\p{L}\p{N}\s']/gu, " ").split(/\s+/).filter(Boolean);

/**
 * The largest number of whole words the end of `base` and the start of `chunk` share, or 0.
 *
 * **THE LONGEST OVERLAP WINS, AND IT IS BOUNDED BY THE SHORTER SIDE.** Searching from the longest
 * down means *"I went to the store"* against *"went to the store"* drops three words rather than
 * finding the one-word match first and leaving *"to the store"* duplicated — R21's first-match lesson,
 * which this house pays whenever a scan stops at the first thing that fits rather than the best.
 */
export function speechOverlap(base: string, chunk: string): number {
  const b = compareWords(base);
  const c = compareWords(chunk);
  const max = Math.min(b.length, c.length);
  for (let k = max; k >= 1; k -= 1) {
    let same = true;
    for (let i = 0; i < k; i += 1) if (b[b.length - k + i] !== c[i]) { same = false; break; }
    if (same) return k;
  }
  return 0;
}

/**
 * `chunk` with its first `k` whole words removed, in the member's OWN spelling.
 *
 * The count comes from the normalised comparison above; the cut is made on the real string, so her
 * capitalisation and punctuation survive intact for every word this keeps.
 */
export function dropLeadingWords(chunk: string, k: number): string {
  if (!(k > 0)) return String(chunk || "");
  const parts = String(chunk || "").trim().split(/\s+/);
  return parts.slice(k).join(" ");
}

/**
 * **THE FIRST FINAL AFTER A RESTART, JOINED WITHOUT ITS ECHO.**
 *
 * Returns the state and, beside it, what was dropped — because a silent de-duplicator is one nobody
 * can prove is working, and *"it dropped nothing"* and *"it was not asked"* are different facts (R18).
 * The hook does not read `dropped`; `scripts/w1-dictation.mjs` does.
 */
export function dictationHandover(state: DictationState, event: DictationEvent): { state: DictationState; dropped: number } {
  const text = String(event?.text || "").trim();
  if (!event?.final || !text) return { state: dictationApply(state, event), dropped: 0 };
  const dropped = speechOverlap(state.committed, text);
  const kept = dropLeadingWords(text, dropped);
  return { state: dictationApply(state, { text: kept, final: true }), dropped };
}

// ---------------------------------------------------------------------------
// IT #314 — **"YOU CAN SAY A SENTENCE AND IT WILL REPEAT IT FIFTY TIMES IN ESSAY FORM."**
//
// Measured on the live store, and the essay was HERS, not Lala's: her dictated messages read *"hey
// hey hey Lala hey Lala hey Lala so hey Lala so in hey Lala so in the ..."*, up to 74,282 characters
// for one thought. That is a phone's recogniser (Chrome on Android) reporting every final as THE
// WHOLE UTTERANCE SO FAR — "hey", then "hey Lala", then "hey Lala so" — each at a new result index,
// so `through` sees each one as new speech and every one of them was appended.
//
// **SO A FINAL THAT BEGINS WITH EVERY WORD OF THE ONE BEFORE IT IS THAT ONE, GROWN, AND ONLY THE
// GROWTH IS KEPT.** A desktop recogniser never does this (each final is the next phrase), so it is
// untouched. **The trade is named:** a member who says a phrase and then, as its own separate phrase,
// the same words again with more after them keeps only one copy of the repeated start. Rarer than
// fifty copies of every sentence by a long way, and the direction R19 allows this error to go.
// Whole words, punctuation and case set aside, exactly as `speechOverlap` compares them.
// ---------------------------------------------------------------------------

/** `next` minus every word of `previous` when it begins with all of them; otherwise `next` as it is. */
export function speechExtension(previous: string, next: string): { text: string; grown: boolean } {
  const p = compareWords(previous);
  const n = compareWords(next);
  if (!p.length || n.length < p.length) return { text: String(next || ""), grown: false };
  for (let i = 0; i < p.length; i += 1) if (p[i] !== n[i]) return { text: String(next || ""), grown: false };
  return { text: dropLeadingWords(next, p.length), grown: true };
}

/**
 * The finals of one event joined, each compared with the final before it (`last`, carried across
 * events by the hook and reset with each recogniser). Returns the text to commit and the new `last`.
 */
export function joinFinals(last: string, finals: string[]): { text: string; last: string } {
  let text = "";
  let previous = String(last || "");
  for (const f of Array.isArray(finals) ? finals : []) {
    if (!String(f || "").trim()) continue;
    text = joinSpoken(text, speechExtension(previous, f).text);
    previous = f;
  }
  return { text, last: previous };
}
